import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Wand2, Layers, Globe2, Target, Search, LineChart as LineChartIcon,
  Brain, Zap, BarChart3, Image as ImageIcon, Camera, MessageSquare, ArrowUpRight,
} from "lucide-react";
import MarketingShell from "../components/marketing/MarketingShell";
import DashboardPreview from "../components/marketing/DashboardPreview";
import DemoModal from "../components/marketing/DemoModal";

const SECTIONS = [
  {
    id: "auto-listing", tag: "Listings",
    title: "AI Auto Listing in 90 seconds.",
    desc: "Drop a single photo. Claude Sonnet 4.5 Vision detects category from 225+ Myntra/Nykaa/Ajio templates and auto-fills 89 columns — brand, fabric, HSN, neck, sleeve, occasion, SEO title (≤90 chars), description and 6-10 keyword tags. You confirm sizes and click Generate.",
    bullets: ["225+ category templates", "HSN auto-inference", "Per-size measurement tables", "Marketplace-native Excel export"],
    icon: Sparkles,
  },
  {
    id: "title-desc", tag: "Copy",
    title: "AI Title &amp; Description Generation.",
    desc: "SEO-tuned product titles, 200-character descriptions and a clean keyword set — written per marketplace style guide. No more 30-minute manual writeups per SKU.",
    bullets: ["Title ≤ 90 chars (Myntra spec)", "Description ≤ 200 chars", "6–10 keyword tags per SKU", "AEO/GEO-aware copy structure"],
    icon: Wand2,
  },
  {
    id: "bulk", tag: "Scale",
    title: "Bulk Listing for entire drops.",
    desc: "Multi-folder uploader: one folder = one SKU. Stack 50 products into a queue, AI processes them in parallel, and you walk away with a single marketplace-ready spreadsheet.",
    bullets: ["Multi-folder queue", "Auto image resize to 2400×3200", "Parallel AI processing", "One Excel out, all 50 SKUs"],
    icon: Layers,
  },
  {
    id: "marketplaces", tag: "Integrations",
    title: "7 Marketplaces. 1 Schema.",
    desc: "Maintain one canonical SKU master. Project it into Myntra, Nykaa, Ajio, Amazon, Flipkart, Swiggy Instamart and Blinkit formats with a single click each.",
    bullets: ["Myntra · Nykaa · Ajio", "Amazon India · Flipkart", "Swiggy Instamart · Blinkit", "Per-platform validation built-in"],
    icon: Globe2,
  },
  {
    id: "ads", tag: "Performance",
    title: "Ad Optimization &amp; ROAS targeting.",
    desc: "Every SKU gets a SCALE / TEST / AVOID bucket based on your real order data. Bid suggestions and budget allocation rules ship in your weekly growth report.",
    bullets: ["SCALE / TEST / AVOID buckets", "ROAS target per category", "Budget reallocation rules", "Ad waste detection"],
    icon: Target,
  },
  {
    id: "competitor", tag: "Intelligence",
    title: "Competitor Analysis &amp; Price Sniper.",
    desc: "Track rival SKUs by URL on Myntra, Nykaa or Blinkit. We re-scrape on schedule, compute price delta, and email you the diff table whenever a competitor moves more than 5%.",
    bullets: ["Up to 10 watched URLs", "Auto re-scrape every 6 hours", "Price-delta email alerts", "30-entry price history"],
    icon: Search,
  },
  {
    id: "trends", tag: "Discovery",
    title: "Product Trend Analysis.",
    desc: "Category-level demand curves built from public Myntra/Nykaa data. See which colours, fabrics or article types are surging this week vs last — so you launch the right SKUs.",
    bullets: ["Week-over-week deltas", "Colour / fabric trend curves", "Q-commerce velocity index", "Surging-keyword discovery"],
    icon: LineChartIcon,
  },
  {
    id: "growth", tag: "Strategy",
    title: "Brand Growth Insights.",
    desc: "Ranked action items by impact × effort. AI tells you which listing to re-shoot first, which title to fix, and which pricing band to test — based on your actual conversion data.",
    bullets: ["Impact × effort ranking", "Per-SKU recommended actions", "Conversion gap analysis", "Weekly auto-report"],
    icon: Brain,
  },
  {
    id: "seo", tag: "Visibility",
    title: "SEO &amp; AEO/GEO Optimization.",
    desc: "On-page audit, 90-char title checker, keyword density score, FAQPage schema generator and a robots.txt that opens you up to ChatGPT, Gemini and Perplexity crawlers.",
    bullets: ["On-page SEO audit", "AEO-ready FAQ schema", "Title length + keyword check", "AI bot allow-list templates"],
    icon: Zap,
  },
  {
    id: "analytics", tag: "Clarity",
    title: "Smart Analytics Dashboard.",
    desc: "Unified GMV, ROAS, RTO and conversion across every channel. Beautiful, instant and exportable. Six-sheet AI Excel report lands in your inbox every Monday.",
    bullets: ["All-channel unified view", "6-sheet AI Excel reports", "Email delivery scheduling", "Mobile-friendly dashboard"],
    icon: BarChart3,
  },
  {
    id: "whatsapp", tag: "B2C",
    title: "WhatsApp Catalog Generator.",
    desc: "Publish your catalog as a beautiful WhatsApp-shareable mini-store. Direct buy-links, view counter, and a built-in lead form — all from one upload.",
    bullets: ["Shareable wa.me link", "View counter analytics", "Mobile-first layout", "Built-in lead capture"],
    icon: MessageSquare,
  },
];

export default function Features() {
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "Features — EcomCatalog AI · AI Listings & Analytics";
  }, []);

  return (
    <MarketingShell onBookDemo={() => setDemoOpen(true)}>
      {/* Hero */}
      <section className="pt-16 sm:pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Features
            </div>
            <h1 className="font-mkt-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter mt-4 leading-[1.02]">
              Everything an ecommerce brand needs,{" "}
              <span className="mkt-gradient-text-vivid">in one workspace.</span>
            </h1>
            <p className="text-lg text-slate-400 mt-6 max-w-2xl leading-relaxed">
              From AI listings to ad optimisation, competitor watch and
              brand-growth analytics — every workflow your team needs, powered by AI.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="mkt-btn-gradient" data-testid="feat-hero-start">
                Start Free Trial <ArrowUpRight size={14} />
              </Link>
              <Link to="/pricing#pricing" className="mkt-btn-ghost" data-testid="feat-hero-monthly">
                Take Monthly Plan
              </Link>
            </div>
          </div>
          <div className="mt-14">
            <DashboardPreview />
          </div>
        </div>
      </section>

      {/* Alternating deep-dive sections */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-28 pb-12">
        {SECTIONS.map((s, i) => {
          const Icon = s.icon;
          const reverse = i % 2 === 1;
          return (
            <section
              key={s.id}
              id={s.id}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center scroll-mt-24"
              data-testid={`feat-section-${s.id}`}
            >
              <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mkt-glass text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
                  {s.tag}
                </div>
                <h2
                  className="font-mkt-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tighter mt-4 leading-[1.05]"
                  dangerouslySetInnerHTML={{ __html: s.title }}
                />
                <p className="text-base text-slate-400 mt-5 leading-relaxed">{s.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                <div className="mkt-glass mkt-hover-lift p-10 sm:p-14 aspect-[5/4] flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-30 mkt-grid-bg pointer-events-none" />
                  <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-indigo-500/15 blur-3xl" />
                  <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-purple-500/15 blur-3xl" />
                  <div className="relative w-32 h-32 rounded-3xl bg-gradient-to-br from-indigo-500/30 to-purple-500/30 border border-white/15 grid place-items-center shadow-[0_20px_60px_-20px_rgba(124,58,237,0.55)]">
                    <Icon size={56} className="text-indigo-200" />
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass-strong p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-purple-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <h3 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                Ready to <span className="mkt-gradient-text-vivid">scale faster?</span>
              </h3>
              <p className="text-slate-400 mt-4 max-w-xl mx-auto">
                Set up your first AI listing in 90 seconds. Talk to sales for onboarding.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/login" className="mkt-btn-gradient">
                  Get Started <ArrowUpRight size={14} />
                </Link>
                <Link to="/pricing#pricing" className="mkt-btn-ghost" data-testid="feat-cta-monthly">
                  Take Monthly Plan
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </MarketingShell>
  );
}
